import {useQueryClient} from "@tanstack/react-query";
import {useAppDispatch, useAppSelector} from "./reduxHooks.ts";
import {authReducerActions} from "../reducer/authReducer.ts";
import {rememberSupportContext, saveSupportContext} from "../utils/supportContextStorage.ts";
import {pushSupportContextEntry} from "../utils/supportContextHistory.ts";
import type {SupportContext} from "../model/user/SupportContext.ts";

/**
 * Enter or leave the context of a garage or group (#56913). Cached data of the
 * previous context is dropped, and the user starts on the start page of the new view.
 */
export const useSupportContext = () => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const context = useAppSelector(state => state.auth.supportContext);
    const accountId = useAppSelector(state => state.auth.ownScope?.accountId);

    const switchTo = (next: SupportContext | null, path = "/") => {
        saveSupportContext(next);
        if (next && accountId) rememberSupportContext(accountId, next);
        // drop the old context's data before the new context renders anything
        queryClient.clear();
        // Every context gets its own router, which starts at the current URL: add the
        // history entry first. It keeps the context for browser back and forward.
        pushSupportContextEntry(path, next);
        dispatch(next ? authReducerActions.startSupportContext(next) : authReducerActions.endSupportContext());
    };

    return {
        context,
        // path: where to land in the new context, e.g. a user found in the admin user list
        enter: (next: SupportContext, path?: string) => { switchTo(next, path); },
        leave: () => { switchTo(null); },
    };
};
